/** @file Settings rebinding rule: one binding's new chord becomes a new override list, or a problem to show. */
import { applyOverrides } from '@pose2d/shortcuts/applyOverrides';
import { parseKeys } from '@pose2d/shortcuts/parseKeys';
import type { Binding, BindingError, Platform } from '@pose2d/shortcuts/types';
import { validateTable } from '@pose2d/shortcuts/validateTable';

/** Why a chord cannot be used; `other` names the binding a CONFLICT is with. */
export interface RebindProblem {
  code: BindingError['code'];
  keys: string;
  other?: string;
}

/** The overrides to save, or the problem. */
export type RebindResult = { ok: true; overrides: Binding[] } | { ok: false; problem: RebindProblem };

type Target = Pick<Binding, 'id' | 'context'>;

const same = (a: Target, b: Target): boolean => a.id === b.id && a.context === b.context;

/** Comparable form of a chord (Mod resolved, modifier flags and key code), or null when it does not parse. */
function chordKey(keys: string, platform: Platform): string | null {
  try {
    const p = parseKeys(keys, platform);
    return [p.ctrl, p.meta, p.shift, p.alt, p.code].join('|');
  } catch {
    return null;
  }
}

/** The binding of `target`'s context that already uses `chord` once every other override is applied. */
function clashOf(defaults: readonly Binding[], rest: readonly Binding[], target: Target, chord: string, platform: Platform): Binding | undefined {
  const when = defaults.find((b) => same(b, target))?.when;
  const others = defaults.filter((b) => b.context === target.context && !same(b, target));
  return applyOverrides(others, rest, platform).table.find((b) => b.when === when && chordKey(b.keys, platform) === chord);
}

/** The override list with `target`'s entry replaced in place, appended when new, removed when `keys` is its default. */
function withOverride(base: Binding | undefined, overrides: readonly Binding[], target: Target, keys: string): Binding[] {
  if (base?.keys === keys) return overrides.filter((o) => !same(o, target));
  const entry = { id: target.id, context: target.context, keys };
  return overrides.some((o) => same(o, target)) ? overrides.map((o) => (same(o, target) ? entry : o)) : [...overrides, entry];
}

/** `''` unbinds and the default's own keys reset; otherwise the chord must parse, not be reserved and not clash. */
export function rebindShortcut(defaults: readonly Binding[], overrides: readonly Binding[], target: Target, keys: string, platform: Platform): RebindResult {
  const base = defaults.find((b) => same(b, target));
  const next = withOverride(base, overrides, target, keys);
  if (keys !== '') {
    const chord = chordKey(keys, platform);
    if (chord === null) return { ok: false, problem: { code: 'INVALID_CHORD', keys } };
    const reserved = validateTable([{ id: target.id, context: target.context, keys }], platform).some((e) => e.code === 'RESERVED_KEY');
    if (reserved) return { ok: false, problem: { code: 'RESERVED_KEY', keys } };
    const clash = clashOf(defaults, overrides.filter((o) => !same(o, target)), target, chord, platform);
    if (clash !== undefined) return { ok: false, problem: { code: 'CONFLICT', keys, other: clash.id } };
  }
  const before = new Set(applyOverrides(defaults, overrides, platform).dropped.map((b) => `${b.context}\u0000${b.id}`));
  const lost = applyOverrides(defaults, next, platform).dropped.find((b) => !same(b, target) && !before.has(`${b.context}\u0000${b.id}`));
  return lost === undefined ? { ok: true, overrides: next } : { ok: false, problem: { code: 'CONFLICT', keys, other: lost.id } };
}
